import PasswordField from '@components/v2/form/PasswordField/PasswordField';
import TextField from '@components/v2/form/TextField/TextField';
import AuthPanel from '@components/v2/layout/AuthPanel/AuthPanel';
import Button from '@components/v2/ui/Button/Button';
import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import useLoginForm, { LoginFormValues } from '../../../../components/LoginForm/useLoginForm';
import LangEnum from '../../../../lang/LangEnum';
import { LoginPageTestIds } from '../../../../utils/testIds/LoginPage.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import useLoginPanelStyles from './loginPanel.styles';

interface LoginPanelProps {
  baseUrl: string;
  onSubmit: (values: LoginFormValues) => Promise<void>;
  slots?: {
    afterSubmit?: ReactNode;
  };
}

const LoginPanel = ({ baseUrl, onSubmit, slots }: LoginPanelProps) => {
  const { classes } = useLoginPanelStyles();
  const { translate } = useTranslation();
  const { formMethods, handleSubmit } = useLoginForm({ onSubmit });
  const {
    control,
    formState: { isSubmitting },
  } = formMethods;

  return (
    <AuthPanel>
      <form onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.fields}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="email"
                autoComplete="email"
                label={translate(LangEnum.LOGIN_EMAIL)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{ htmlInput: { 'test-id': LoginPageTestIds.LOGIN_EMAIL } }}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => (
              <PasswordField
                {...field}
                autoComplete="current-password"
                label={translate(LangEnum.LOGIN_PASSWORD)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{ htmlInput: { 'test-id': LoginPageTestIds.LOGIN_PASSWORD } }}
              />
            )}
          />
        </div>
        <Button
          variant="text"
          color="secondary"
          size="small"
          className={classes.forgotPassword}
          to={`${baseUrl}/recover-password`}
        >
          {translate(LangEnum.LOGIN_I_FORGOT_MY_PASSWORD)}
        </Button>
        <div className={classes.actions}>
          <Button
            testId={LoginPageTestIds.ENTER_BUTTON}
            variant="contained"
            size="large"
            color="primary"
            type="submit"
            loading={isSubmitting}
            fullWidth
          >
            {translate(LangEnum.LOGIN_LOGIN)}
          </Button>
          {slots?.afterSubmit}
          <Button
            variant="text"
            color="secondary"
            size="large"
            fullWidth
            to={`${baseUrl}/store-sign-up`}
          >
            {translate(LangEnum.LOGIN_REGISTER_MY_STORE)}
          </Button>
        </div>
      </form>
    </AuthPanel>
  );
};

export default LoginPanel;
